/*----------------------------------
- MENU ROW
----------------------------------*/

import type { IconName } from '@uniquedomains/components/ui/icon';
import Icon from '@/client/components/ui/icon/Icon';

/*----------------------------------
- TYPES
----------------------------------*/

type MenuRowProps = {
    label: string;
    description: string;
    iconName: IconName;
    /**
     * Absent marks a destination that does not exist yet. The row then renders as a
     * disabled control with a Soon pill instead of an anchor, so a crawler never counts
     * it and a keyboard user never lands on a page that was never built.
     */
    href?: string;
    /** What assistive technology hears in place of the Soon pill. Required with no `href`. */
    status?: string;
    /** Short note on the right of a desktop row: a fact, or the action the row performs. */
    trailing?: string;
    /** `action` draws the trailing note as the row's call to action rather than a quiet fact. */
    trailingTone?: 'quiet' | 'action';
    /** `ink` marks a tool, `soft` a list or a market surface. */
    tile?: 'ink' | 'soft';
    /** Desktop rows sit in a hover grid; phone rows are full-width touch targets. */
    density?: 'desktop' | 'mobile';
    onClick?: () => void;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const TILE_CLASS = {
    ink: 'bg-[#1b1d1e] text-[#f2e97e]',
    soft: 'bg-[#f1ece6] text-[#1b1d1e]',
} as const;

const DISABLED_TILE_CLASS = 'border border-dashed border-[#cfcbc0] bg-white text-[#66696b]';

// `h-full` on the desktop row: rows sit in a two-column grid, so a neighbour whose
// description wraps sets the row height. Without it the shorter row's icon tile floats
// above its partner's, and the two columns read as two misaligned lists.
const ROW_CLASS = {
    desktop: '-mx-3 h-full gap-3.5 rounded-[14px] p-3',
    mobile: 'gap-3 rounded-2xl px-1 py-2',
} as const;

const LABEL_CLASS = {
    desktop: 'text-[15.5px]',
    mobile: 'text-[15px] tracking-[-0.01em]',
} as const;

const TILE_SIZE_CLASS = {
    desktop: 'h-10 w-10',
    mobile: 'h-9 w-9',
} as const;

const SOON_PILL_CLASS =
    'rounded-full bg-[#edeae2] px-[7px] py-0.5 text-[9px] font-extrabold uppercase tracking-[0.06em] text-[#66696b]';

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * Icon tile, label, one-line description and an optional trailing note: the one row the
 * Domains lists, the Market menu and the Tools menu are built from, on desktop and on a
 * phone. One component rather than two keeps the two layouts from drifting apart.
 */
export default function MenuRow({
    label,
    description,
    iconName,
    href,
    status,
    trailing,
    trailingTone = 'quiet',
    tile = 'soft',
    density = 'desktop',
    onClick,
}: MenuRowProps) {
    const tileClass = `inline-flex ${TILE_SIZE_CLASS[density]} flex-none items-center justify-center rounded-xl`;
    const body = (
        <>
            <span class="min-w-0 flex-1">
                <span class={`flex items-center gap-2 font-semibold text-slate-950 ${LABEL_CLASS[density]}`}>
                    {label}
                    {href ? null : <span class={SOON_PILL_CLASS}>Soon</span>}
                </span>
                <span class="mt-0.5 block text-[12.5px] leading-[1.45] text-[#66696b]">{description}</span>
            </span>
            {trailing && density === 'desktop' ? (
                <span
                    class={[
                        'flex-none whitespace-nowrap',
                        trailingTone === 'action'
                            ? 'text-[13px] font-bold text-[#1b1d1e]'
                            : 'text-xs font-semibold text-[#66696b]',
                    ].join(' ')}
                >
                    {trailing}
                </span>
            ) : null}
        </>
    );

    if (!href) {
        return (
            <button
                type="button"
                disabled
                aria-label={`${label}, ${status}`}
                class={`flex w-full cursor-not-allowed items-center text-left ${ROW_CLASS[density]}`}
            >
                <span class={`${tileClass} ${DISABLED_TILE_CLASS}`}>
                    <Icon name={iconName} size={density === 'desktop' ? 18 : 16} strokeWidth={1.8} />
                </span>
                {body}
            </button>
        );
    }

    return (
        <a
            href={href}
            onClick={onClick}
            class={`flex items-center transition-colors hover:bg-[#f0ede6] ${ROW_CLASS[density]}`}
        >
            <span class={`${tileClass} ${TILE_CLASS[tile]}`}>
                <Icon name={iconName} size={density === 'desktop' ? 18 : 16} strokeWidth={1.8} />
            </span>
            {body}
        </a>
    );
}
